import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import React from "react";
import IconifyIcon from "../IconifyIcon";
import { useAppSelector } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import "./table-component.css";

export interface TableData {
  header: { label: string; isIcon: boolean; tooltipLabel: string }[];
  body: {
    row: number;
    cells: {
      label: { mainLabel: string; tooltipLabel: string }[];
      isIcon: boolean;
      isBadge?: boolean;
      isButton: boolean;
      buttonFunction?: () => void;
      disabledButton?: boolean;
      color?: string;
      backgroundColor?: string;
      display: "flex-dir-row" | "flex-dir-col";
      secondaryButton?: {
        label: string;
        buttonFunction: () => void;
      };
    }[];
  }[];
}

interface TableComponentProps {
  selectableRow: boolean;
  data: TableData | null;
  onPressRow: (id: number) => void;
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (rowsPerPage: number) => void;
  total: number;
  tableHeight: string;
  emptyMessage?: string;
}

const TableComponent = ({
  selectableRow,
  data,
  onPressRow,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  total,
  tableHeight,
  emptyMessage,
}: TableComponentProps) => {
  const labels = useAppSelector(selectLabels);
  const isEmpty = !data?.body?.length;
  const emptyStateMessage = emptyMessage || labels.no_records;

  const [isScrolledToBottom, setIsScrolledToBottom] = React.useState(false);
  const [tooltipOpen, setTooltipOpen] = React.useState<Record<string, boolean>>(
    {}
  );

  const tableContainerRef: any = React.useRef(null);

  const handleScroll = () => {
    if (tableContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        tableContainerRef.current;
      setIsScrolledToBottom(scrollTop + clientHeight >= scrollHeight);
    }
  };

  const handleTooltipToggle = (key: string, open: boolean) => {
    setTooltipOpen((prev) => {
      // Cierra todos los tooltips antes de abrir el actual
      const newState = Object.keys(prev).reduce((acc, k) => {
        acc[k] = false;
        return acc;
      }, {} as Record<string, boolean>);

      // Abre solo el tooltip correspondiente si `open` es true
      if (open) newState[key] = true;

      return newState;
    });
  };

  React.useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (tableContainer) {
        tableContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <Box
      style={{
        width: "100%",
        boxSizing: "border-box",
        border: "1px #e0e0e0 solid",
        borderRadius: 6,
        overflow: "hidden",
      }}
    >
      <TableContainer
        ref={tableContainerRef}
        sx={{ maxHeight: tableHeight, minHeight: tableHeight }}
        style={{ overflowX: "auto", backgroundColor: "#f8f8f8" }}
      >
        <Table stickyHeader style={{ width: "100%" }}>
          <TableHead>
            <TableRow>
              {data?.header.map((item, index) => (
                <TableCell
                  key={`${index}-${item?.label}`}
                  align="center"
                  className="p-5"
                  style={{ backgroundColor: "#f0f0f0" }}
                >
                  {item?.isIcon ? (
                    item?.tooltipLabel ? (
                      <Tooltip title={item?.tooltipLabel} arrow placement="top">
                        <div>
                          <IconifyIcon
                            icon={item?.label}
                            className="title-color"
                          />
                        </div>
                      </Tooltip>
                    ) : (
                      <IconifyIcon icon={item?.label} className="title-color" />
                    )
                  ) : item?.tooltipLabel ? (
                    <Tooltip title={item?.tooltipLabel} arrow placement="top">
                      <div>
                        <p>{item?.label}</p>
                      </div>
                    </Tooltip>
                  ) : (
                    <p>{item?.label}</p>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isEmpty ? (
              <TableRow className="no-selectable-row">
                <TableCell
                  colSpan={Math.max(data?.header?.length || 1, 1)}
                  align="center"
                  className="p-5"
                  style={{ height: 120, borderBottom: "none" }}
                >
                  <p className="body-text-md title-color">{emptyStateMessage}</p>
                </TableCell>
              </TableRow>
            ) : (
              data?.body.map((item, index) => (
              <TableRow
                key={`${index}-${item?.row}`}
                className={selectableRow ? "body-row" : "no-selectable-row"}
                onClick={selectableRow ? () => onPressRow(item?.row) : () => {}}
              >
                {item?.cells.map((cellItem, cellIndex) => (
                  <TableCell
                    key={`${cellIndex}-${cellItem?.label}`}
                    align="center"
                    className="p-5"
                  >
                    {cellItem?.isBadge ? (
                      <div
                        className={`flex ${cellItem?.display} justify-content-center gap-3`}
                      >
                        {cellItem?.label?.map((cellLabel, cellLabelIndex) => (
                          <Chip
                            key={`${cellLabelIndex}`}
                            label={cellLabel?.mainLabel}
                            size="small"
                            sx={{
                              color: cellItem?.color || "#1f2937",
                              backgroundColor:
                                cellItem?.backgroundColor || "#e5e7eb",
                              fontWeight: 600,
                            }}
                          />
                        ))}
                      </div>
                    ) : cellItem?.isIcon ? (
                      cellItem?.label?.some(
                        (cellLabel) => cellLabel?.tooltipLabel !== ""
                      ) ? (
                        <div
                          className={`flex ${cellItem?.display} justify-content-center gap-3`}
                        >
                          {cellItem?.label.map((cellLabel, cellLabelIndex) => (
                            <Tooltip
                              key={`${cellLabelIndex}`}
                              title={cellLabel?.tooltipLabel}
                              arrow
                              placement="top"
                              open={
                                tooltipOpen[`${index}-${cellIndex}-${cellLabelIndex}`] ||
                                false
                              }
                              onOpen={() =>
                                handleTooltipToggle(
                                  `${index}-${cellIndex}-${cellLabelIndex}`,
                                  true
                                )
                              }
                              onClose={() =>
                                handleTooltipToggle(
                                  `${index}-${cellIndex}-${cellLabelIndex}`,
                                  false
                                )
                              }
                            >
                              <div className="flex align-items-center title-color">
                                <IconifyIcon
                                  icon={cellLabel?.mainLabel}
                                  style={{
                                    cursor: cellItem?.buttonFunction
                                      ? "pointer"
                                      : "default",
                                  }}
                                  onClick={
                                    cellItem?.buttonFunction
                                      ? (event) => {
                                          event.stopPropagation();
                                          handleTooltipToggle(
                                            `${index}-${cellIndex}-${cellLabelIndex}`,
                                            false
                                          );
                                          cellItem.buttonFunction!();
                                        }
                                      : undefined
                                  }
                                />
                              </div>
                            </Tooltip>
                          ))}
                        </div>
                      ) : (
                        <div
                          className={`flex ${cellItem?.display} justify-content-center gap-3`}
                        >
                          {cellItem.label.map((cellLabel, cellLabelIndex) => (
                            <IconifyIcon
                              key={`${cellLabelIndex}`}
                              icon={cellLabel?.mainLabel}
                              className="title-color"
                              style={{
                                cursor: cellItem?.buttonFunction
                                  ? "pointer"
                                  : "default",
                              }}
                              onClick={
                                cellItem?.buttonFunction
                                  ? (event) => {
                                      event.stopPropagation();
                                      cellItem.buttonFunction!();
                                    }
                                  : undefined
                              }
                            />
                          ))}
                        </div>
                      )
                    ) : cellItem?.isButton ? (
                      <Button
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (cellItem.buttonFunction) {
                            cellItem.buttonFunction();
                          }
                        }}
                        disabled={cellItem?.disabledButton}
                      >
                        {cellItem?.label.map((cellLabel, cellLabelIndex) => (
                          <p key={`${cellLabelIndex}`} style={{ fontSize: 12 }}>
                            {cellLabel?.mainLabel}
                          </p>
                        ))}
                      </Button>
                    ) : cellItem?.label?.some(
                        (cellLabel) => cellLabel?.tooltipLabel !== ""
                      ) ? (
                      <div
                        className={`flex ${cellItem?.display} justify-content-center align-items-center ${
                          cellItem.secondaryButton ? "gap-1" : "gap-3"
                        }`}
                      >
                        {cellItem.label.map((cellLabel, cellLabelIndex) => (
                          <Tooltip
                            key={`${cellLabelIndex}`}
                            title={cellLabel?.tooltipLabel}
                            arrow
                            placement="top"
                          >
                            <div>
                              <p
                                className="body-text-md"
                                style={{ color: cellItem?.color }}
                              >
                                {cellLabel?.mainLabel}
                              </p>
                            </div>
                          </Tooltip>
                        ))}
                        {cellItem.secondaryButton && (
                          <Button
                            size="small"
                            variant="text"
                            onClick={(event) => {
                              event.stopPropagation();
                              cellItem.secondaryButton?.buttonFunction();
                            }}
                            sx={{
                              fontSize: 11,
                              minWidth: 0,
                              padding: "2px 4px",
                              textTransform: "none",
                              lineHeight: 1.2,
                            }}
                          >
                            {cellItem.secondaryButton.label}
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div
                        className={`flex ${cellItem?.display} justify-content-center align-items-center ${
                          cellItem.secondaryButton ? "gap-1" : "gap-3"
                        }`}
                      >
                        {cellItem?.label?.map((cellLabel, cellLabelIndex) => (
                          <p
                            key={`${cellLabelIndex}`}
                            className="body-text-xs"
                            style={{ color: cellItem?.color }}
                          >
                            {cellLabel?.mainLabel}
                          </p>
                        ))}
                        {cellItem.secondaryButton && (
                          <Button
                            size="small"
                            variant="text"
                            onClick={(event) => {
                              event.stopPropagation();
                              cellItem.secondaryButton?.buttonFunction();
                            }}
                            sx={{
                              fontSize: 11,
                              minWidth: 0,
                              padding: "2px 4px",
                              textTransform: "none",
                              lineHeight: 1.2,
                            }}
                          >
                            {cellItem.secondaryButton.label}
                          </Button>
                        )}
                      </div>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 20, 30, 50]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={labels.rows_per_page}
        labelDisplayedRows={function defaultLabelDisplayedRows({
          from,
          to,
          count,
        }) {
          return `${from}–${to} ${labels.of} ${
            count !== -1 ? count : `more than ${to}`
          }`;
        }}
        style={{ borderTop: isScrolledToBottom ? "none" : "1px #e0e0e0 solid" }}
      />
    </Box>
  );
};

export default TableComponent;
